import {
  PropertyLocaltion,
  NewSoldOut as SoldOutIcon,
  PropertyDefaultImg as DefaultImageIcon,
  PropertySigned,
  PropertyNotSigned,
} from 'common/components/svgs';
/* eslint-disable jsx-control-statements/jsx-use-if-tag */
import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Skeleton } from 'antd';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import { Hosts } from '~helpers/environments';
import generatePath from '~settings/routing';
import { propertyState } from '~constants';
import { trackClickPropertyListPage } from '~pages/pms-dashboard/properties/track';
import PendingProgress from 'common/components/pending-progress';

const imageHost = Hosts.imageUrl;

const useStyles = makeStyles(
  theme => ({
    cardBox: {
      width: '100%',
      ...theme.flex.row,
      marginBottom: '12px',
      background: '#FFFFFF',
    },
    stickImgBox: {
      width: '182px',
      height: '112px',
      position: 'relative',
      overflow: 'hidden',
      flexShrink: 0,
    },
    stickImg: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
    textOverFlow: {
      whiteSpace: 'nowrap',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    contentBox: {
      ...theme.flex.fillRow,
      minWidth: 0,
      '&>div': {
        flex: 1,
        marginRight: '56px',
        paddingTop: '32px',
        paddingBottom: '30px',
        '& > div': {
          width: '100%',
          ...theme.singleLineOverflow,
        },
        '&:first-child': {
          flex: 2,
          minWidth: 120,
          marginLeft: '24px',
        },
        '&:nth-child(2)': {
          minWidth: 106,
        },
        '&:nth-child(3)': {
          minWidth: 64,
        },
        '&:last-child': {
          minWidth: 112,
          marginRight: '24px',
        },
      },
    },
    statusBox: {
      padding: '2px 8px',
      borderRadius: '2px',
      position: 'absolute',
      left: '8px',
      top: '8px',
      fontSize: '12px',
      lineHeight: '20px',
      border: '1px solid blue',
    },
    new: {
      background: theme.colors.blue1,
      color: theme.colors.blue6,
      borderColor: theme.colors.blue4,
    },
    editing: {
      background: theme.colors.blue1,
      color: theme.colors.blue6,
      borderColor: theme.colors.blue4,
    },
    published: {
      background: theme.colors.mainGreen1,
      color: theme.colors.mainGreen6,
      borderColor: theme.colors.mainGreen3,
    },
    unpublished: {
      background: theme.colors.gray2,
      color: theme.colors.captionColor,
      borderColor: theme.colors.gray5,
    },

    name: {
      ...theme.typography.h4,
      marginBottom: '4px',
      color: theme.colors.titleColor,
    },
    address: {
      ...theme.typography.body2,
      color: theme.colors.bodyColor,
    },
    bookingType: {
      ...theme.typography.body2,
      color: theme.colors.disableColor,
      marginBottom: '4px',
    },
    constactSvg: {
      verticalAlign: 'middle',
      marginRight: '4px',
    },
    locationSvg: {
      verticalAlign: 'middle',
      marginRight: '9px',
    },

    description: {
      ...theme.typography.body2,
      color: theme.colors.titleColor,
    },
    soldoutImg: {
      opacity: 0.4,
    },
  }),
  { name: 'new-property-card' },
);

const Status = ({ status }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { statusBox } = classes;
  const statusClass = status.toLowerCase();

  return (
    <div className={classNames(statusBox, classes[statusClass])}>
      {t(`cms:cms.property_card.status.tag.${statusClass}`)}
    </div>
  );
};

const NewPropertyCard = props => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { property, loading } = props;
  const paymentUrl = generatePath('paymentList');
  const payoutUrl = generatePath('withdrawal.list');
  const paymentMethodUrl = generatePath(
    'property.edit',
    { propertySlug: property?.slug },
    { section: 'PAYMENT_METHOD' },
  );

  const {
    cardBox,
    stickImgBox,
    contentBox,
    stickImg,
    name,
    address,
    bookingType,
    description,
    constactSvg,
    locationSvg,
    soldoutImg,
  } = classes;
  return (
    <Link
      to={
        property && property.slug
          ? generatePath('property.homepage', {
              propertySlug: property.slug,
            })
          : ''
      }
      disabled={!property || !property.slug}
    >
      <div className={cardBox} onClick={() => trackClickPropertyListPage('Property Card')}>
        <Skeleton
          active
          avatar={{
            size: 122,
            shape: 'square',
          }}
          loading={loading}
        >
          <div
            className={classNames(stickImgBox, {
              [soldoutImg]: get(property, 'state') === propertyState.SOLD_OUT,
            })}
          >
            <If condition={!property.image}>
              <DefaultImageIcon className={stickImg} />
            </If>
            <If condition={get(property, 'state') === propertyState.SOLD_OUT}>
              <SoldOutIcon className="property-card__sold-out" />
            </If>
            <If condition={property.image}>
              <img
                className="property-card__image"
                src={`${
                  property.image ? `${imageHost}/250x250/${property.image.node.source}` : ''
                }`}
                alt={property.slug}
              />
            </If>
            {property.statuses && <Status status={property.statuses} />}
          </div>
          <div className={contentBox}>
            <div>
              <div className={name}>{property.name}</div>

              <div>
                <PropertyLocaltion className={locationSvg} />
                <span className={address}>{`${property.cityName}, ${property.countryName}`}</span>
              </div>
            </div>
            <div>
              <div className={bookingType}>{t('cms:properties_booking_type_title')}</div>
              <div className={description}>
                {t(`cms:properties_booking_type_${property.bookingJourney?.toLowerCase()}`)}
              </div>
            </div>
            <div>
              <div className={bookingType}>{t('cms:properties_contract_stage_title')}</div>
              <div className={description}>
                {property.contractStage === 'DUALLY_SIGNED' ? (
                  <PropertySigned className={constactSvg} />
                ) : (
                  <PropertyNotSigned className={constactSvg} />
                )}{' '}
                {property.contractStage === 'DUALLY_SIGNED'
                  ? t('cms:properties_contract_stage_signed_desc')
                  : t('cms:properties_contract_stage_not_signed_desc')}
              </div>
            </div>
            <div>
              <div className={bookingType}>{t('cms:properties_pending_alert')}</div>
              <If condition={property}>
                <PendingProgress
                  isCMS={false}
                  payoutUrl={payoutUrl}
                  paymentUrl={paymentUrl}
                  paymentMethodUrl={paymentMethodUrl}
                  property={property}
                />
              </If>
            </div>
          </div>
        </Skeleton>
      </div>
    </Link>
  );
};

Status.propTypes = {
  status: PropTypes.string.isRequired,
};
NewPropertyCard.propTypes = {
  loading: PropTypes.bool.isRequired,
  property: PropTypes.object.isRequired,
};
export default NewPropertyCard;
